import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** `'all'` means the staff-wide, unfiltered view (every store's rows, with a Store column) —
 *  the same behavior the catalog pages had before this switcher existed. Anything else is a
 *  store id. Only ever read/set for staff; a vendor's scope is always implicitly their own
 *  store, resolved server-side. */
export type StoreScopeSelection = string;

export interface StoreScopeState {
  selectedStoreId: StoreScopeSelection;
}

const STORAGE_KEY = 'giftsay-admin:selected-store-id';

function readPersisted(): StoreScopeSelection {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? 'all';
  } catch {
    // Private browsing / storage disabled — fall back to the unfiltered view.
    return 'all';
  }
}

const initialState: StoreScopeState = {
  selectedStoreId: readPersisted(),
};

const storeScopeSlice = createSlice({
  name: 'storeScope',
  initialState,
  reducers: {
    setSelectedStoreId(state, action: PayloadAction<StoreScopeSelection>) {
      state.selectedStoreId = action.payload;
      try {
        localStorage.setItem(STORAGE_KEY, action.payload);
      } catch {
        // Same as above — losing the persisted preference isn't worth failing the action over.
      }
    },
  },
});

export const { setSelectedStoreId } = storeScopeSlice.actions;
export const storeScopeReducer = storeScopeSlice.reducer;
